// --- Redux ---
import {combineReducers} from '@reduxjs/toolkit';
// --- Module Reducers ---
import {app, autoLogout, indicators} from './modules/app/reducers';
import {current, icd10s, patients, tags} from './modules/patients/reducers';
import {ecgDetail, ecgs, measurements} from './modules/patients/modules/measurements/reducers';
import {surveys} from './modules/patients/modules/surveys/reducers';
import {studies} from './modules/patients/modules/studies/reducers';
import {scheduledVisites} from './modules/dashboard/reducers';
import {feelingFactors} from './modules/system/modules/feeling_factors/reducers';
import {roles} from './modules/system/modules/roles/reducers';
import {systemSurveys} from './modules/system/modules/surveys/reducers';
import {systemStudies} from "src/modules/system/modules/studies/reducers";
import {currentUser} from './modules/system/reducers/CurrentUser';
import {systemCounts} from './modules/system/reducers/SystemCount';
import {legals} from './modules/legals/reducers/Legals';
import {login} from './modules/login/reducers/Login';
import responsibility from './modules/login/reducers/Responsibility';
import {unlock} from './modules/unlock/reducers';
import {editPassword, newPassword, passwordReset, physicianCheck} from './modules/password/reducers';
import {physicians} from './modules/physicians/reducers/Physicians';
import {chat, chats} from './modules/messages/reducers';
import {help} from './modules/help/reducers';
import {appPasswordReset} from "./modules/password/modules/app/reducers";
import {exportsReducer} from './modules/patients/modules/export/reducers';
import {otp} from './modules/otp/reducers';
import {visite} from "./modules/patients/modules/visite/reducers/Visite";
import {triggerAdherenceEvents, triggerEvents} from "./modules/patients/modules/triggers/modules/events/reducers";
import {manageContext} from "./modules/manage/reducers";
import {secret} from "helpers/reducers/Secret";
import {videoConsultations as physicianConsultations, videoConsultationSession} from "./modules/video_consultation/reducers";
import {session} from "./modules/auth/reducers/Auth";
import {contact} from "./modules/physicians/modules/ticket/reducers/ContactForm";
import {loginCandidates} from "src/modules/tenant_login/reducers/LoginCandidates";
import {browsers} from "src/modules/system/modules/browsers/reducers/Browsers";
import {countries} from "./modules/countries/reducers";
import filter from "shared/filter/reducer";
import openingHours from "./modules/system/reducers/OpeningHours";
import profilePicture from "./modules/system/reducers/ProfilePicture";
import people from "src/modules/people/reducer";
import hotkeys from "src/modules/app/reducers/Hotkeys";
import onlineStatus from "src/modules/app/reducers/OnlineStatus";
import institutions from "src/modules/institutions/reducer";
import {sensorPackages} from "src/modules/patients/modules/sensor_packages/reducers/SensorPackages";
import {callLogger} from "./modules/call_logging/reducers/CallLoggerDialog";
import {programs} from './modules/system/modules/programs/reducers/Programs';
import {addresses} from 'shared/address/reducers/Address';

const measurementContext = combineReducers({
  ecgs: ecgs,
  ecgDetail: ecgDetail
});

const triggersContext = combineReducers({
  triggerEvents,
  triggerAdherenceEvents: triggerAdherenceEvents
});

const patientContext = combineReducers({
  current: current,
  sensorPackages: sensorPackages,
  measurements: measurements,
  exports: exportsReducer,
  measurementContext: measurementContext,
  triggersContext: triggersContext,
  surveysContext: combineReducers({
    surveys: surveys
  }),
  studiesContext: combineReducers({
    studies: studies
  }),
  visite: visite
});

const patientsContext = combineReducers({
  patients: patients,
  tags: tags,
  icd10s: icd10s,
  scheduledVisites: scheduledVisites
});

const systemContext = combineReducers({
  institutions,
  roles: roles,
  onlineStatus,
  systemCounts: systemCounts,
  surveysContext: combineReducers({
    surveys: systemSurveys
  }),
  studiesContext: combineReducers({
    studies: systemStudies
  }),
  currentUser: currentUser,
  legals: legals,
  feelingFactors: feelingFactors,
  browsers: browsers,
  openingHours,
  profilePicture,
  people,
  programs,
  addresses
});

const authContext = combineReducers({
  session: session,
  login: secret(login),
  otp: otp,
  //NOTE(Keune): This is secret, so that the timer and interval initialization is reset with a logout
  autoLogout: secret(autoLogout),
  unlock: unlock,
  passwordReset: passwordReset,
  responsibility: secret(responsibility),
  appPasswordReset: appPasswordReset,
  editPassword: editPassword,
  physicianCheck: physicianCheck,
  newPassword: newPassword
});

const chatContext = combineReducers({
  chats: chats,
  chat: chat
});

const videoConsultationContext = combineReducers({
  session: videoConsultationSession,
  videoConsultations: physicianConsultations
});

const reducers = {
  app: app,
  hotkeys,
  help: help,
  callLogger: callLogger,
  countries: countries,
  contact: contact,
  indicators: secret(indicators),
  filter,
  loginCandidates: loginCandidates,
  authContext: authContext,
  videoConsultationContext: videoConsultationContext,
  patientsContext: secret(patientsContext),
  physiciansContext: secret(physicians),
  patientContext: secret(patientContext),
  systemContext: secret(systemContext),
  chatContext: secret(chatContext),
  manageContext: secret(manageContext)
};

export default reducers;
